import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { X, Sparkles, Camera, Share2, FolderPlus, Users, ChevronRight } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

/**
 * Onboarding Hints System
 * Lightweight, non-intrusive contextual guidance for first-time actions.
 * 
 * Hint Types:
 * A) First profile edit - 3-step hint: "Start with AI" -> "Add 1 photo" -> "Publish profile"
 * B) First visit to Projects page (enterprise) - Highlight "Create Project" + AI option
 * C) First time on public profile share page - "Complete your profile to get discovered"
 * D) Empty states - CTAs for empty projects/talentboard
 * 
 * Rules:
 * - Show each tooltip only once per user (stored in DB)
 * - Allow "Don't show again" / Dismiss
 */

// Hint configurations
const HINT_CONFIGS = {
  profile_edit_hints: {
    steps: [
      {
        key: "ai_start",
        icon: Sparkles,
        title: "Start with AI",
        description: "Let AI help you write a professional bio, headline, and suggest skills.",
        color: "violet"
      },
      {
        key: "add_photo",
        icon: Camera,
        title: "Add a Photo",
        description: "Profiles with photos get 10x more views. Upload a professional headshot.",
        color: "blue"
      },
      {
        key: "publish",
        icon: Share2,
        title: "Publish Your Profile",
        description: "Once you're ready, save and publish to start getting discovered.",
        color: "green"
      }
    ]
  },
  projects_page: {
    icon: FolderPlus,
    title: "Create Your First Project",
    description: "Start by creating a project. You can use AI to help generate the content!",
    color: "blue"
  },
  public_profile_share: {
    icon: Share2,
    title: "Complete Your Profile",
    description: "A complete profile helps you get discovered. Add more details to stand out!",
    linkText: "Edit Profile",
    color: "violet"
  },
  empty_projects: {
    icon: FolderPlus,
    title: "No Projects Yet",
    description: "Create your first project with AI assistance.",
    ctaText: "Create Project (AI-assisted)",
    color: "blue"
  },
  empty_talentboard: {
    icon: Users,
    title: "Your TalentBoard is Empty",
    description: "Browse talents and add them to your shortlist.",
    ctaText: "Browse Talents",
    ctaLink: "/browse",
    color: "violet"
  }
};

/**
 * Single hint tooltip component
 */
function HintTooltip({ config, onDismiss, onAction, className = "", position = "bottom" }) {
  const Icon = config.icon;
  const colorClasses = {
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700"
  };

  const positionClasses = {
    bottom: "top-full mt-2",
    top: "bottom-full mb-2",
    left: "right-full mr-2",
    right: "left-full ml-2"
  };

  return (
    <div 
      className={`absolute z-50 ${positionClasses[position]} ${className}`}
      data-testid={`onboarding-hint-${config.key || config.title.toLowerCase().replace(/\s+/g, '-')}`}
    >
      <div className={`p-3 rounded-lg border shadow-lg max-w-xs ${colorClasses[config.color] || colorClasses.violet}`}>
        <div className="flex items-start gap-2">
          <Icon className="w-5 h-5 flex-shrink-0 mt-0.5" />
          <div className="flex-1">
            <p className="font-medium text-sm">{config.title}</p>
            <p className="text-xs mt-1 opacity-80">{config.description}</p>
            
            {config.ctaText && (
              <Button 
                size="sm" 
                className="mt-2 h-7 text-xs"
                onClick={onAction}
                data-testid="onboarding-hint-cta"
              >
                {config.ctaText}
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
            )}
            
            {config.linkText && (
              <button 
                className="text-xs mt-2 underline hover:no-underline"
                onClick={onAction}
              >
                {config.linkText} →
              </button>
            )}
          </div>
          
          <button 
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 p-0.5"
            aria-label="Dismiss hint"
            data-testid="onboarding-hint-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        <button 
          onClick={onDismiss}
          className="text-xs mt-2 opacity-60 hover:opacity-100"
        >
          Don't show again
        </button>
        
        {/* Arrow pointer */}
        <div className={`absolute w-3 h-3 rotate-45 ${colorClasses[config.color] || colorClasses.violet} border-t border-l
          ${position === 'bottom' ? '-top-1.5 left-4' : ''}
          ${position === 'top' ? '-bottom-1.5 left-4 border-t-0 border-l-0 border-b border-r' : ''}
        `} />
      </div>
    </div>
  );
}

/**
 * Multi-step hint component (for profile edit flow)
 */
function MultiStepHint({ steps, currentStep = 0, onDismiss, onStepAction, className = "" }) {
  const currentConfig = steps[currentStep];
  if (!currentConfig) return null;
  
  const Icon = currentConfig.icon;
  const colorClasses = {
    violet: "bg-violet-50 border-violet-200 text-violet-700",
    blue: "bg-blue-50 border-blue-200 text-blue-700",
    green: "bg-green-50 border-green-200 text-green-700"
  };

  return (
    <div 
      className={`relative z-50 ${className}`}
      data-testid="onboarding-multi-step-hint"
    >
      <div className={`p-4 rounded-lg border shadow-lg ${colorClasses[currentConfig.color]}`}>
        <div className="flex items-start gap-3">
          <div className={`w-8 h-8 rounded-full flex items-center justify-center bg-white/60`}>
            <Icon className="w-4 h-4" />
          </div>
          <div className="flex-1">
            <div className="flex items-center gap-2 mb-1">
              <p className="font-medium text-sm">{currentConfig.title}</p>
              <span className="text-xs opacity-60">Step {currentStep + 1} of {steps.length}</span>
            </div>
            <p className="text-xs opacity-80">{currentConfig.description}</p>
          </div>
          
          <button 
            onClick={onDismiss}
            className="text-gray-400 hover:text-gray-600 p-0.5"
            aria-label="Dismiss hints"
            data-testid="onboarding-multi-hint-dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
        
        {/* Progress dots */}
        <div className="flex items-center justify-center gap-1.5 mt-3">
          {steps.map((_, i) => (
            <div 
              key={i} 
              className={`w-2 h-2 rounded-full transition-colors ${
                i === currentStep ? 'bg-current' : 'bg-current/30'
              }`}
            />
          ))}
        </div>
        
        <div className="flex items-center justify-between mt-3 pt-2 border-t border-current/20">
          <button 
            onClick={onDismiss}
            className="text-xs opacity-60 hover:opacity-100"
          >
            Skip all
          </button>
          
          <Button 
            size="sm" 
            variant="ghost"
            className="h-7 text-xs"
            onClick={() => onStepAction(currentConfig.key)}
          >
            Got it
            <ChevronRight className="w-3 h-3 ml-1" />
          </Button>
        </div>
      </div>
    </div>
  );
}

/**
 * Hook to manage onboarding hints state
 */
export function useOnboardingHints() {
  const [hintsState, setHintsState] = useState({});
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchHintsState();
  }, []);

  const fetchHintsState = async () => {
    try {
      const token = localStorage.getItem("token");
      if (!token) {
        setLoading(false);
        return;
      }

      const response = await fetch(`${API_URL}/api/onboarding/hints`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      if (response.ok) {
        const data = await response.json();
        setHintsState(data.hints_dismissed || {});
      }
    } catch (error) {
      console.error("Failed to fetch onboarding hints:", error);
    } finally {
      setLoading(false);
    }
  };

  const dismissHint = async (hintKey) => {
    try {
      const token = localStorage.getItem("token");
      if (!token) return;

      // Optimistic update
      setHintsState(prev => ({ ...prev, [hintKey]: true }));

      await fetch(`${API_URL}/api/onboarding/hints/dismiss`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify({ hint_key: hintKey, dismissed: true })
      });
    } catch (error) {
      console.error("Failed to dismiss hint:", error);
    }
  };

  const shouldShowHint = (hintKey) => {
    return !loading && !hintsState[hintKey];
  };

  return {
    hintsState,
    loading,
    dismissHint,
    shouldShowHint,
    refreshHints: fetchHintsState
  };
}

/**
 * Profile Edit Hints Component
 * Shows the 3-step hint flow for first-time profile editors
 */
export function ProfileEditHints({ onAIClick, onPhotoClick, onPublishClick, className = "" }) {
  const { shouldShowHint, dismissHint } = useOnboardingHints();
  const [currentStep, setCurrentStep] = useState(0);

  if (!shouldShowHint("profile_edit_hints")) return null;

  const steps = HINT_CONFIGS.profile_edit_hints.steps;

  const handleStepAction = (stepKey) => {
    if (stepKey === "ai_start" && onAIClick) {
      onAIClick();
    } else if (stepKey === "add_photo" && onPhotoClick) {
      onPhotoClick();
    } else if (stepKey === "publish" && onPublishClick) {
      onPublishClick();
    }
    
    // Move to next step or dismiss
    if (currentStep < steps.length - 1) {
      setCurrentStep(prev => prev + 1);
    } else {
      dismissHint("profile_edit_hints");
    }
  };

  const handleDismiss = () => {
    dismissHint("profile_edit_hints");
  };

  return (
    <MultiStepHint
      steps={steps}
      currentStep={currentStep}
      onDismiss={handleDismiss}
      onStepAction={handleStepAction}
      className={className}
    />
  );
}

/**
 * Projects Page Hint Component
 * Shows hint to create first project for enterprise users
 */
export function ProjectsPageHint({ onCreateClick, className = "" }) {
  const { shouldShowHint, dismissHint } = useOnboardingHints();

  if (!shouldShowHint("projects_page")) return null;

  return (
    <HintTooltip
      config={HINT_CONFIGS.projects_page}
      onDismiss={() => dismissHint("projects_page")}
      onAction={onCreateClick}
      className={className}
      position="bottom"
    />
  );
}

/**
 * Empty State Hint Component
 * Shows CTA when a list is empty
 */
export function EmptyStateHint({ type, onAction, className = "" }) {
  const { shouldShowHint, dismissHint } = useOnboardingHints();
  const config = HINT_CONFIGS[type];

  if (!config || !shouldShowHint(type)) return null;

  const Icon = config.icon;
  const colorClasses = {
    violet: "border-violet-200 bg-violet-50/50",
    blue: "border-blue-200 bg-blue-50/50"
  };

  return (
    <div 
      className={`text-center py-8 px-4 rounded-lg border-2 border-dashed ${colorClasses[config.color]} ${className}`}
      data-testid={`empty-state-hint-${type}`}
    >
      <Icon className={`w-12 h-12 mx-auto mb-3 text-${config.color}-400`} />
      <h3 className="font-medium text-gray-800">{config.title}</h3>
      <p className="text-sm text-gray-600 mt-1 mb-4">{config.description}</p>
      
      <div className="flex items-center justify-center gap-3">
        <Button 
          onClick={onAction}
          className={`bg-${config.color}-600 hover:bg-${config.color}-700`}
          data-testid="empty-state-cta"
        >
          <Sparkles className="w-4 h-4 mr-2" />
          {config.ctaText}
        </Button>
        
        <button 
          onClick={() => dismissHint(type)}
          className="text-xs text-gray-500 hover:text-gray-700"
        >
          Dismiss
        </button>
      </div>
    </div>
  );
}

/**
 * Public Profile Share Hint Component
 * Shows hint when user views their own public profile for the first time
 * "Complete your profile to get discovered"
 */
export function PublicProfileShareHint({ onEditClick, className = "" }) {
  const { shouldShowHint, dismissHint } = useOnboardingHints();

  if (!shouldShowHint("public_profile_share")) return null;

  return (
    <div 
      className={`relative ${className}`}
      data-testid="public-profile-share-hint"
    >
      <div className="bg-gradient-to-r from-violet-50 to-purple-50 border border-violet-200 rounded-xl p-4 shadow-sm">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center flex-shrink-0">
            <Share2 className="w-5 h-5 text-violet-600" />
          </div>
          <div className="flex-1 min-w-0">
            <h4 className="font-medium text-violet-900 text-sm">
              {HINT_CONFIGS.public_profile_share.title}
            </h4>
            <p className="text-xs text-violet-700/80 mt-1">
              {HINT_CONFIGS.public_profile_share.description}
            </p>
            <div className="flex items-center gap-2 mt-3">
              <Button 
                size="sm" 
                onClick={onEditClick}
                className="h-7 text-xs bg-violet-600 hover:bg-violet-700"
                data-testid="public-profile-hint-edit-btn"
              >
                Edit Profile
                <ChevronRight className="w-3 h-3 ml-1" />
              </Button>
              <button 
                onClick={() => dismissHint("public_profile_share")}
                className="text-xs text-violet-600 hover:text-violet-800"
                data-testid="public-profile-hint-dismiss"
              >
                Don't show again
              </button>
            </div>
          </div>
          <button 
            onClick={() => dismissHint("public_profile_share")}
            className="text-violet-400 hover:text-violet-600 p-0.5 flex-shrink-0"
            aria-label="Dismiss hint"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      </div>
    </div>
  );
}

export default {
  HintTooltip,
  MultiStepHint,
  useOnboardingHints,
  ProfileEditHints,
  ProjectsPageHint,
  EmptyStateHint,
  PublicProfileShareHint,
  HINT_CONFIGS
};
